import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Briefcase, MapPin, DollarSign, Calendar, 
  CheckCircle, XCircle, MessageSquare, Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TalentOffers = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOffers();
  }, []);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/talent/me/offers`);
      setOffers(response.data.offers || []);
    } catch (error) {
      toast.error('Failed to load offers');
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (offerId, response) => {
    try {
      await axios.patch(`${API}/talent/offers/${offerId}/respond`, { response });
      toast.success(`Offer ${response}`);
      fetchOffers();
    } catch (error) {
      toast.error('Failed to respond to offer');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: <Badge className="bg-blue-100 text-blue-800">Sent</Badge>,
      viewed: <Badge className="bg-yellow-100 text-yellow-800">Viewed</Badge>,
      accepted: <Badge className="bg-green-100 text-green-800">Accepted</Badge>,
      declined: <Badge className="bg-red-100 text-red-800">Declined</Badge>,
      expired: <Badge className="bg-gray-100 text-gray-800">Expired</Badge>
    };
    return badges[status] || <Badge>{status}</Badge>;
  };

  const getDaysUntilExpiry = (expiresAt) => {
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    return days > 0 ? days : 0;
  };

  const isExpired = (expiresAt) => {
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Job Offers</h1>
          <p className="text-muted-foreground">Manage offers you've received from employers</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading offers...</div>
        ) : offers.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-semibold mb-2">No Offers Yet</h3>
              <p className="text-muted-foreground">
                When employers send you job offers, they'll appear here
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {offers.map((offer) => {
              const expired = isExpired(offer.expires_at);
              const daysLeft = getDaysUntilExpiry(offer.expires_at);
              const canRespond = !expired && offer.status === 'sent' || offer.status === 'viewed';

              return (
                <Card key={offer.id} className="border-2 border-border">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-foreground mb-2">{offer.role_title}</h3>
                        <div className="text-sm text-muted-foreground">
                          From: {offer.company?.company_name || 'Company'}
                        </div>
                      </div>
                      {getStatusBadge(offer.status)}
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>{offer.location_suburb}, {offer.location_state}</span>
                        <Badge className="text-xs capitalize">{offer.work_arrangement.replace('_', ' ')}</Badge>
                      </div>

                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-muted-foreground" />
                        <Badge className="text-xs capitalize">{offer.work_type.replace('_', '-')}</Badge>
                      </div>

                      {offer.salary_min && offer.salary_max && (
                        <div className="flex items-center gap-2 text-sm font-semibold text-green-600">
                          <DollarSign className="w-4 h-4" />
                          <span>
                            ${(offer.salary_min / 1000).toFixed(0)}k - ${(offer.salary_max / 1000).toFixed(0)}k per {offer.salary_unit}
                          </span>
                        </div>
                      )}

                      {offer.start_date && (
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>Start: {new Date(offer.start_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>

                    {offer.message && (
                      <div className="bg-muted/50 rounded-lg p-4 mb-4">
                        <p className="text-sm text-foreground whitespace-pre-line">{offer.message}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-4 border-t border-border">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {expired ? (
                          <span className="text-red-600">Expired</span>
                        ) : (
                          <span>Expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>
                        )}
                      </div>

                      {canRespond && (
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRespond(offer.id, 'accepted')}
                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Accept
                          </button>
                          <button
                            onClick={() => handleRespond(offer.id, 'declined')}
                            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium"
                          >
                            <XCircle className="w-4 h-4" />
                            Decline
                          </button>
                          <button
                            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted text-sm font-medium"
                          >
                            <MessageSquare className="w-4 h-4" />
                            Ask
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="text-xs text-muted-foreground mt-2">
                      Received {new Date(offer.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TalentOffers;
